import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Package, Palette, Building2, Users, Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LangSwitcher from '@/components/LangSwitcher';

const OPTIONS = [
    {
        icon: Package,
        title: 'Pack Kit Projet sectoriel',
        price: '+100 €',
        priceSub: 'unique · par pack',
        tagline: 'Templates métier prêts à l\'emploi pour démarrer 10× plus vite.',
        bullets: [
            '15 documents .docx prêts à compléter (cadrage, budget, rapport bailleur…)',
            'Modèles spécifiques par secteur : Énergie, Santé, Agriculture, Mines, ONG…',
            'Mise à jour gratuite à chaque nouvelle version',
            'Activable instantanément sur votre espace après paiement',
        ],
        included: 'Inclus d\'office dans Pro et Illimité',
        eligibleTiers: ['Explorer', 'Essentiel'],
        testid: 'option-pack',
    },
    {
        icon: Palette,
        title: 'Marque blanche',
        price: '+100 €',
        priceSub: 'unique',
        tagline: 'Votre identité, vos clients. Personnalisez l\'interface aux couleurs de votre organisation.',
        bullets: [
            'Logo personnalisé sur toutes les pages',
            'Palette de couleurs adaptée à votre charte',
            'En-têtes/pieds de page sur les exports PDF',
            'Sous-domaine sur demande (selon plan)',
        ],
        included: 'Inclus d\'office dans Pro et Illimité',
        eligibleTiers: ['Explorer', 'Essentiel'],
        testid: 'option-whitelabel',
    },
    {
        icon: Building2,
        title: '+2 espaces supplémentaires',
        price: '+50 €',
        priceSub: 'unique',
        tagline: 'Plus de filiales, plus de programmes ? Étendez votre quota d\'espaces sans changer de plan.',
        bullets: [
            'Ajoute 2 espaces clients/projets supplémentaires à votre quota',
            'Cumulable autant de fois que nécessaire',
            'Activation immédiate après validation du paiement',
            'Aucun engagement de durée — paiement unique',
        ],
        included: 'Disponible pour Explorer et Essentiel',
        eligibleTiers: ['Explorer', 'Essentiel'],
        testid: 'option-spaces',
    },
    {
        icon: Users,
        title: '+5 utilisateurs supplémentaires',
        price: '+50 €',
        priceSub: 'unique',
        tagline: 'L\'équipe grandit. Ajoutez 5 sièges utilisateur à votre plan, sans surcoût récurrent.',
        bullets: [
            'Ajoute 5 utilisateurs au quota global de votre compte',
            'Accès complet aux modules de votre plan',
            'Cumulable et activable à tout moment',
            'Aucun engagement de durée — paiement unique',
        ],
        included: 'Disponible pour Explorer et Essentiel',
        eligibleTiers: ['Explorer', 'Essentiel'],
        testid: 'option-users',
    },
];

export default function Options() {
    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="page-options">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="options-logo-home">
                        <img src="/african-valley-logo.png" alt="African Valley" className="h-11 w-11 rounded-lg object-cover ring-1 ring-border shadow-soft" />
                        <span className="flex items-baseline gap-2">
                            <span className="font-heading text-2xl font-semibold tracking-tight">African</span>
                            <span className="font-heading text-2xl text-primary font-medium">Valley</span>
                            <span className="font-heading text-xs uppercase tracking-[0.18em] text-foreground/50 ml-1.5 hidden sm:inline">Projets 360°</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <LangSwitcher />
                        <Link to="/" className="text-sm text-foreground/70 hover:text-foreground inline-flex items-center gap-1">
                            <ArrowLeft size={14} /> Accueil
                        </Link>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-6xl mx-auto px-6 py-14 lg:py-20 w-full">
                {/* Hero */}
                <div className="max-w-3xl">
                    <div className="eyebrow mb-5 text-primary">Options à la carte</div>
                    <h1 className="font-heading text-4xl lg:text-5xl leading-tight">
                        Étendez votre plan <span className="text-primary">sans changer d'abonnement.</span>
                    </h1>
                    <p className="mt-7 text-lg text-foreground/75 leading-[1.8]">
                        Quatre options ponctuelles, en <strong>paiement unique</strong>, pour ajuster African Valley
                        aux besoins exacts de votre organisation. Pas d'engagement, pas de surcoût récurrent.
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-secondary/30 bg-secondary-light text-secondary text-xs">
                        <Sparkles size={12} /> Disponibles pour les offres <strong>Explorer</strong> et <strong>Essentiel</strong>
                    </div>
                </div>

                {/* Cards */}
                <div className="grid md:grid-cols-2 gap-6 mt-16">
                    {OPTIONS.map((opt) => (
                        <article key={opt.testid} className="card-soft p-8 flex flex-col" data-testid={opt.testid}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="h-12 w-12 rounded-lg bg-primary-light text-primary flex items-center justify-center flex-shrink-0">
                                    <opt.icon size={22} strokeWidth={1.6} />
                                </div>
                                <div className="text-right">
                                    <div className="font-heading text-3xl text-primary leading-none">{opt.price}</div>
                                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1.5">{opt.priceSub}</div>
                                </div>
                            </div>
                            <h2 className="font-heading text-2xl mt-5">{opt.title}</h2>
                            <p className="text-foreground/70 mt-3 leading-[1.7]">{opt.tagline}</p>
                            <ul className="mt-6 space-y-3 text-sm flex-1">
                                {opt.bullets.map((b, i) => (
                                    <li key={i} className="flex items-start gap-2.5">
                                        <Check size={14} className="text-secondary mt-1 flex-shrink-0" />
                                        <span className="text-foreground/85">{b}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-6 pt-5 border-t border-border text-xs text-muted-foreground">
                                💡 {opt.included}
                            </div>
                        </article>
                    ))}
                </div>

                {/* How it works */}
                <section className="mt-20 card-soft p-8 lg:p-12 bg-surface-muted/40 border-secondary/20">
                    <div className="grid lg:grid-cols-3 gap-10">
                        <div>
                            <div className="eyebrow mb-3 text-secondary">Comment ça marche ?</div>
                            <h3 className="font-heading text-2xl">Ajouter une option en 3 étapes</h3>
                        </div>
                        <ol className="lg:col-span-2 grid sm:grid-cols-3 gap-6 list-none">
                            {[
                                { n: '1', t: 'Connexion', s: 'Connectez-vous à votre espace African Valley (ou créez un compte gratuit).' },
                                { n: '2', t: 'Paiement Stripe', s: 'Depuis votre tableau de bord, bouton « Upgrade plan » → Options à l\'unité → paiement sécurisé.' },
                                { n: '3', t: 'Activation instantanée', s: 'L\'option est activée immédiatement après la confirmation du paiement.' },
                            ].map((step) => (
                                <li key={step.n}>
                                    <div className="font-heading text-3xl text-primary leading-none">{step.n}</div>
                                    <div className="font-medium mt-3">{step.t}</div>
                                    <div className="text-sm text-foreground/70 mt-2 leading-[1.7]">{step.s}</div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* CTA */}
                <section className="mt-16 text-center">
                    <h3 className="font-heading text-3xl">Une question sur les options ?</h3>
                    <p className="text-foreground/70 mt-3 max-w-xl mx-auto leading-[1.7]">
                        Notre équipe vous répond sous 48 h pour vous aider à choisir la combinaison la plus adaptée.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        <Link to="/p/contact">
                            <Button size="lg" className="bg-primary hover:bg-primary-hover" data-testid="options-cta-contact">
                                Nous contacter <ArrowRight size={16} className="ml-2" />
                            </Button>
                        </Link>
                        <Link to="/#pricing">
                            <Button size="lg" variant="outline" data-testid="options-cta-pricing">
                                Voir les plans
                            </Button>
                        </Link>
                    </div>
                </section>
            </main>
        </div>
    );
}
